import { InvoiceGroup, PaymentMethodType, PaymentMethodTypeV2 } from '@css-xp/graphtypes/src/generated-types-new';
import { COMMERCIAL_PRICE_ELIGIBILITY, ELIGIBLE_BILLING_PROFILES_FOR_AMENDMENT } from '@css-xp/ppc-constants';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import { isAchPaymentMethod, isPaypalPaymentMethod } from '@css-xp/ppc-utils';

export const getDisableReason = (
  isDisabledBasedOnPricingEligibilityAllowList: boolean,
  isDisabledBasedOnPaymentMethod: boolean,
  isDisabledBasedOnCurrency: boolean
) => {
  if (isDisabledBasedOnPaymentMethod && isDisabledBasedOnPricingEligibilityAllowList) {
    return 'This billing profile is setup with different pricing eligibility and payment method that is not suitable for this transaction';
  }
  if (isDisabledBasedOnPricingEligibilityAllowList && isDisabledBasedOnCurrency) {
    return 'This billing profile is setup with different pricing eligibility and currency';
  }
  if (isDisabledBasedOnPaymentMethod && isDisabledBasedOnCurrency) {
    return 'This billing profile is setup with a payment method and currency that is not suitable for this transaction';
  }
  if (isDisabledBasedOnPricingEligibilityAllowList) {
    return 'This billing profile is setup with different pricing eligibility';
  }
  if (isDisabledBasedOnPaymentMethod) {
    return 'This billing profile is setup with a payment method that is not suitable for this transaction';
  }
  if (isDisabledBasedOnCurrency) {
    return 'This billing profile is setup with a different currency';
  }
  return '';
};

export const getSearchedQueryInBillingProfile = ({
  billingProfile,
  searchQuery,
  isExposeAndSearchByBillingProfileId,
}: {
  billingProfile: InvoiceGroup;
  searchQuery: string;
  isExposeAndSearchByBillingProfileId?: boolean;
}) => {
  const shipToPartyData =
    billingProfile?.name +
    ' ' +
    billingProfile?.shipToParty?.name +
    ' ' +
    billingProfile?.shipToParty?.addressLine1 +
    ' ' +
    billingProfile?.shipToParty?.addressLine2 +
    ' ' +
    billingProfile?.shipToParty?.city +
    ' ' +
    billingProfile?.shipToParty?.state +
    ' ' +
    billingProfile?.shipToParty?.country +
    ' ' +
    billingProfile?.shipToParty?.postcode;
  return isExposeAndSearchByBillingProfileId
    ? shipToPartyData.toLowerCase()?.includes(searchQuery.toLowerCase()) ||
        billingProfile.id?.includes(searchQuery.toLowerCase())
    : shipToPartyData.toLowerCase()?.includes(searchQuery.toLowerCase());
};

export const getIsEnabledBasedOnThePaymentMethod = ({
  billingProfile,
  isDeferredPaymentMethodDisabled,
  isNoPaymentMethodDisabled,
  isPaypalEnabled,
  isAchPaymentMethodFFEnabled,
}: {
  billingProfile: InvoiceGroup;
  isDeferredPaymentMethodDisabled: boolean;
  isNoPaymentMethodDisabled: boolean;
  isPaypalEnabled: boolean;
  isAchPaymentMethodFFEnabled?: boolean;
}) => {
  let isEnabledBasedOnPaymentMethod = true;

  if (isDeferredPaymentMethodDisabled && isNoPaymentMethodDisabled) {
    isEnabledBasedOnPaymentMethod = !!(
      billingProfile?.defaultPaymentMethod?.type === PaymentMethodType.CARD ||
      //@ts-ignore
      (isPaypalEnabled && billingProfile?.defaultPaymentMethod?.type === PaymentMethodTypeV2.PAYPAL) ||
      (isAchPaymentMethodFFEnabled && isAchPaymentMethod(billingProfile?.defaultPaymentMethod?.type))
    );
  } else if (isNoPaymentMethodDisabled) {
    isEnabledBasedOnPaymentMethod = !!billingProfile?.defaultPaymentMethod?.type;
  } else if (isDeferredPaymentMethodDisabled) {
    if (!isPaypalEnabled && isPaypalPaymentMethod(billingProfile?.defaultPaymentMethod?.type)) {
      isEnabledBasedOnPaymentMethod = false;
    } else if (!isAchPaymentMethodFFEnabled && isAchPaymentMethod(billingProfile?.defaultPaymentMethod?.type)) {
      isEnabledBasedOnPaymentMethod = false;
    } else {
      isEnabledBasedOnPaymentMethod = billingProfile?.defaultPaymentMethod?.type !== PaymentMethodType.DEFERRED;
    }
  }
  return isEnabledBasedOnPaymentMethod;
};

export const getIsEnabledBasedOnPricingEligibility = ({
  areNonCommercialBillingProfilesDisabled,
  billingProfile,
}: {
  areNonCommercialBillingProfilesDisabled?: boolean;
  billingProfile: InvoiceGroup;
}) => {
  if (areNonCommercialBillingProfilesDisabled) {
    return billingProfile?.shipToPriceEligibility
      ? ELIGIBLE_BILLING_PROFILES_FOR_AMENDMENT.includes(billingProfile?.shipToPriceEligibility?.toLowerCase())
      : true;
  }
  return true;
};

export const getIsEnabledBasedOnPricingEligibilityAllowList = ({
  shouldAllowBillingProfileTransferBasedOnPricingEligibility,
  allowedPricingEligibilityForBillingProfileTransfer,
  billingProfile,
}: {
  shouldAllowBillingProfileTransferBasedOnPricingEligibility?: boolean;
  allowedPricingEligibilityForBillingProfileTransfer?: string[];
  billingProfile: InvoiceGroup;
}) => {
  if (shouldAllowBillingProfileTransferBasedOnPricingEligibility) {
    return allowedPricingEligibilityForBillingProfileTransfer
      ?.map((pricingEligibility: string) => pricingEligibility.toLowerCase())
      .includes(defaultTo(get(billingProfile, 'shipToPriceEligibility'), COMMERCIAL_PRICE_ELIGIBILITY).toLowerCase());
  }
  return true;
};
